/**
 * Reschedule Modal Component
 * Phase 8: Reschedule Functionality
 *
 * Allows rescheduling of bookings and recurring instances
 * - Shows current booking details
 * - New date and time selection
 * - Real-time availability checking
 * - Reason for reschedule
 * - Customer notification toggle
 */

import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, AlertCircle, CheckCircle, RefreshCw, Bell } from 'lucide-react';
import Modal from '../ui/Modal';
import { OneTimeBooking, RecurringBookingInstance } from '../../types/bookings';
import { formatDate, formatTime } from '../../utils/bookingHelpers';
import { format } from 'date-fns';

interface OperatingHour {
  id: number;
  day_of_week: string;
  opening_time: string;
  closing_time: string;
  is_closed: boolean;
}

interface RescheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking?: OneTimeBooking;
  recurringInstance?: RecurringBookingInstance;
  onReschedule: (data: RescheduleData) => Promise<void>;
  onCheckAvailability?: (date: string, startTime: string, endTime: string, courtId: number) => Promise<boolean>;
  operatingHours?: OperatingHour[];
}

export interface RescheduleData {
  new_date: string;
  new_start_time: string;
  new_end_time: string;
  reason?: string;
  notify_customer: boolean;
}

export default function RescheduleModal({
  isOpen,
  onClose,
  booking,
  recurringInstance,
  onReschedule,
  onCheckAvailability,
  operatingHours,
}: RescheduleModalProps) {
  // Determine if this is a booking or recurring instance
  const isRecurringInstance = !!recurringInstance;
  const currentDate = isRecurringInstance && recurringInstance
    ? recurringInstance.scheduled_date
    : booking?.booking_date || '';
  const currentStartTime = isRecurringInstance && recurringInstance
    ? recurringInstance.scheduled_start_time
    : booking?.start_time || '09:00:00';
  const currentEndTime = isRecurringInstance && recurringInstance
    ? recurringInstance.scheduled_end_time
    : booking?.end_time || '10:00:00';
  const courtId = isRecurringInstance && recurringInstance
    ? (recurringInstance.recurringBooking?.court_id || 0)
    : (booking?.court_id || 0);

  // Calculate original duration in minutes
  const calculateDuration = (start: string, end: string): number => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    return (endHour * 60 + endMin) - (startHour * 60 + startMin);
  };

  const originalDurationMinutes = calculateDuration(currentStartTime, currentEndTime);

  // Form state
  const [newDate, setNewDate] = useState(currentDate);
  const [newStartTime, setNewStartTime] = useState(currentStartTime.substring(0, 5)); // HH:MM
  const [newEndTime, setNewEndTime] = useState(currentEndTime.substring(0, 5)); // HH:MM
  const [reason, setReason] = useState('');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState<'unchecked' | 'checking' | 'available' | 'unavailable'>('unchecked');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewDate(currentDate);
      setNewStartTime(currentStartTime.substring(0, 5));
      setNewEndTime(currentEndTime.substring(0, 5));
      setReason('');
      setAvailabilityStatus('unchecked');
      setErrors({});
    }
  }, [isOpen, currentDate, currentStartTime, currentEndTime]);

  // Auto-calculate end time when start time changes (keep same duration)
  useEffect(() => {
    if (newStartTime) {
      const [startHour, startMin] = newStartTime.split(':').map(Number);
      const endMinutes = (startHour * 60 + startMin) + originalDurationMinutes;
      const endHour = Math.floor(endMinutes / 60) % 24; // Wrap at 24
      const endMin = endMinutes % 60;

      // Format as HH:MM
      const calculatedEndTime = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
      setNewEndTime(calculatedEndTime);
    }
  }, [newStartTime, originalDurationMinutes]);

  // Generate time options based on venue operating hours for the selected date
  const generateTimeOptions = (selectedDate?: string) => {
    const options: string[] = [];

    // Default hours if no operating hours provided
    let startHour = 6;
    let endHour = 23;

    // Get operating hours for the selected date
    if (selectedDate && operatingHours && operatingHours.length > 0) {
      try {
        const dateObj = new Date(selectedDate + 'T00:00:00');
        const dayOfWeek = format(dateObj, 'EEEE').toLowerCase();

        const daySchedule = operatingHours.find(
          (oh) => oh.day_of_week.toLowerCase() === dayOfWeek
        );

        if (daySchedule && !daySchedule.is_closed && daySchedule.opening_time && daySchedule.closing_time) {
          const openingParts = daySchedule.opening_time.split(':');
          const closingParts = daySchedule.closing_time.split(':');

          startHour = parseInt(openingParts[0], 10);
          endHour = parseInt(closingParts[0], 10);

          // If closing time has minutes > 0, extend end hour
          const closingMinutes = parseInt(closingParts[1], 10) || 0;
          if (closingMinutes > 0) {
            endHour = endHour + 1;
          }

          // Handle overnight hours (e.g., 15:00-00:00 or 18:00-02:00)
          if (endHour <= startHour) {
            endHour = endHour + 24;
          }
        }
      } catch {
        // Fall back to defaults on parse error
      }
    }

    // Generate time slots
    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === endHour && minute > 0) break; // Don't go past closing time
        const displayHour = hour % 24; // Wrap around for overnight hours
        const timeStr = `${String(displayHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        options.push(timeStr);
      }
    }

    return options;
  };

  const timeOptions = generateTimeOptions(newDate);

  // Get today's date for min attribute
  const today = new Date().toISOString().split('T')[0];

  // Check if date/time has changed
  const hasChanged =
    newDate !== currentDate ||
    newStartTime !== currentStartTime.substring(0, 5) ||
    newEndTime !== currentEndTime.substring(0, 5);

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!newDate) {
      newErrors.new_date = 'Date is required';
    }

    // Check if venue is closed on the selected date
    if (newDate && operatingHours) {
      const selectedDate = new Date(newDate + 'T00:00:00');
      const dayOfWeek = format(selectedDate, 'EEEE').toLowerCase();

      const daySchedule = operatingHours.find(
        (oh) => oh.day_of_week.toLowerCase() === dayOfWeek
      );

      if (daySchedule && daySchedule.is_closed) {
        newErrors.new_date = `Venue is closed on ${format(selectedDate, 'EEEE')}s. Please choose a different day.`;
      }
    }

    if (!newStartTime) {
      newErrors.new_start_time = 'Start time is required';
    }

    if (!newEndTime) {
      newErrors.new_end_time = 'End time is required';
    }

    if (newStartTime && newEndTime && newStartTime >= newEndTime) {
      newErrors.new_end_time = 'End time must be after start time';
    }

    if (!reason || !reason.trim()) {
      newErrors.reason = 'Reason for reschedule is required';
    }

    if (!hasChanged) {
      newErrors.general = 'Please select a different date or time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle availability check
  const handleCheckAvailability = async () => {
    if (!onCheckAvailability) {
      setAvailabilityStatus('unchecked');
      return;
    }

    if (!validate()) {
      return;
    }

    setAvailabilityStatus('checking');
    try {
      const isAvailable = await onCheckAvailability(newDate, newStartTime, newEndTime, courtId);
      setAvailabilityStatus(isAvailable ? 'available' : 'unavailable');
    } catch (error) {
      console.error('Availability check failed:', error);
      setAvailabilityStatus('unavailable');
    }
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onReschedule({
        new_date: newDate,
        new_start_time: newStartTime,
        new_end_time: newEndTime,
        reason: reason || undefined,
        notify_customer: true,
      });
      onClose();
    } catch (error: any) {
      setErrors({ general: error.message || 'Failed to reschedule. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Early return after all hooks - proper location for conditional rendering
  if (!booking && !recurringInstance) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" animate={false}>
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
            <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Reschedule {isRecurringInstance ? 'Session' : 'Booking'}</h2>
            <p className="text-xs sm:text-sm text-gray-500">Change the date and time</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Current Details */}
          <div className="bg-gray-50 rounded-xl sm:rounded-lg p-3 sm:p-4 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              Current Schedule
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
              <div>
                <p className="text-gray-500">Date</p>
                <p className="font-medium text-gray-900">{formatDate(currentDate)}</p>
              </div>
              <div>
                <p className="text-gray-500">Time</p>
                <p className="font-medium text-gray-900">
                  {formatTime(currentStartTime)} - {formatTime(currentEndTime)}
                </p>
              </div>
            </div>
          </div>

          {/* New Date */}
          <div>
            <label htmlFor="new_date" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              New Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="new_date"
              value={newDate}
              onChange={(e) => {
                setNewDate(e.target.value);
                setAvailabilityStatus('unchecked');
              }}
              min={today}
              required
              className={`w-full px-3 py-2.5 sm:py-2 border rounded-xl sm:rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                errors.new_date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.new_date && (
              <p className="mt-1 text-xs sm:text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {errors.new_date}
              </p>
            )}
          </div>

          {/* Time Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {/* Start Time */}
            <div>
              <label htmlFor="new_start_time" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                New Start Time <span className="text-red-500">*</span>
              </label>
              <select
                id="new_start_time"
                value={newStartTime}
                onChange={(e) => {
                  setNewStartTime(e.target.value);
                  setAvailabilityStatus('unchecked');
                }}
                required
                className={`w-full px-3 py-2.5 sm:py-2 border rounded-xl sm:rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                  errors.new_start_time ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select start time</option>
                {timeOptions.map((time) => (
                  <option key={time} value={time}>
                    {formatTime(time)}
                  </option>
                ))}
              </select>
              {errors.new_start_time && (
                <p className="mt-1 text-xs sm:text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {errors.new_start_time}
                </p>
              )}
            </div>

            {/* End Time - Auto-calculated based on duration */}
            <div>
              <label htmlFor="new_end_time" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                End Time <span className="text-gray-500 text-xs">(Auto)</span>
              </label>
              <input
                type="text"
                id="new_end_time"
                value={formatTime(newEndTime)}
                disabled
                className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-xl sm:rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Duration: {Math.floor(originalDurationMinutes / 60)}h {originalDurationMinutes % 60}m
              </p>
            </div>
          </div>

          {/* Availability Check Button */}
          {onCheckAvailability && hasChanged && (
            <div>
              <button
                type="button"
                onClick={handleCheckAvailability}
                disabled={availabilityStatus === 'checking'}
                className="w-full px-4 py-2.5 sm:py-2 bg-indigo-600 text-white rounded-xl sm:rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors flex items-center justify-center gap-2 text-sm font-semibold"
              >
                <Clock className="w-4 h-4" />
                {availabilityStatus === 'checking' ? 'Checking...' : 'Check Availability'}
              </button>
            </div>
          )}

          {/* Availability Status */}
          {availabilityStatus === 'available' && (
            <div className="bg-green-50 border border-green-200 rounded-xl sm:rounded-lg p-3 sm:p-4 flex items-center gap-2 text-green-800">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              <p className="font-medium text-sm">This time slot is available!</p>
            </div>
          )}

          {availabilityStatus === 'unavailable' && (
            <div className="bg-red-50 border border-red-200 rounded-xl sm:rounded-lg p-3 sm:p-4 flex items-center gap-2 text-red-800">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              <p className="font-medium text-sm">This slot is not available. Please try a different time.</p>
            </div>
          )}

          {/* Reason */}
          <div>
            <label htmlFor="reason" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Reason for Reschedule <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (errors.reason) {
                  setErrors({ ...errors, reason: '' });
                }
              }}
              rows={3}
              placeholder="Please explain why you are rescheduling..."
              required
              className={`w-full px-3 py-2.5 sm:py-2 border rounded-xl sm:rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm ${
                errors.reason ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.reason && (
              <p className="mt-1 text-xs sm:text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {errors.reason}
              </p>
            )}
          </div>

          {/* Customer Notification Info */}
          <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-blue-50 rounded-xl sm:rounded-lg border border-blue-200">
            <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-900">Customer will be notified</p>
              <p className="text-xs text-gray-600 mt-0.5">
                If the customer has an email, they will receive a notification.
              </p>
            </div>
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-xl sm:rounded-lg p-3 sm:p-4 flex items-center gap-2 text-red-800">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              <p className="text-xs sm:text-sm">{errors.general}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="w-full sm:flex-1 px-4 py-3 sm:py-2.5 bg-gray-200 text-gray-700 rounded-xl sm:rounded-lg font-medium hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !hasChanged || (availabilityStatus === 'unavailable')}
              className="w-full sm:flex-1 px-4 py-3 sm:py-2.5 bg-blue-600 text-white rounded-xl sm:rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Rescheduling...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Reschedule
                </>
              )}
            </button>
          </div>
        </form>
        </div>
    </Modal>
  );
}
